import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(arr) {
  return arr[randInt(0, arr.length - 1)];
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

function isBlank(v) {
  return !v || !v.raw || String(v.raw).trim() === "";
}

function fmtSigned(n) {
  return n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`;
}

function linearExprLatex(a, b, c) {
  // ax + by = c
  const terms = [];
  const pushTerm = (coef, body) => {
    if (coef === 0) return;
    const absCoef = Math.abs(coef);
    const coefPart = absCoef === 1 ? "" : `${absCoef}`;
    const core = `${coefPart}${body}`;
    if (terms.length === 0) {
      terms.push(coef < 0 ? `-${core}` : core);
    } else {
      terms.push(coef < 0 ? `- ${core}` : `+ ${core}`);
    }
  };
  pushTerm(a, "x");
  pushTerm(b, "y");
  if (terms.length === 0) terms.push("0");
  return `${terms.join(" ")} = ${c}`;
}

function substitutionEqLatex(lx, ly, yExpr, lc) {
  // lx*x + ly*(yExpr) = lc, z ladnym zapisem dla Â±1
  const xPart = lx === 1 ? "x" : lx === -1 ? "-x" : `${lx}x`;
  const absLy = Math.abs(ly);
  const yCoeff = absLy === 1 ? "" : `${absLy}`;
  const sign = ly >= 0 ? "+" : "-";
  return `${xPart} ${sign} ${yCoeff}\\left(${yExpr}\\right) = ${lc}`;
}

function polyYToLatex(a, b, c) {
  const terms = [];

  const pushTerm = (coef, body) => {
    if (coef === 0) return;
    const absCoef = Math.abs(coef);
    const coefPart = absCoef === 1 ? "" : `${absCoef}`;
    const core = `${coefPart}${body}`;
    if (terms.length === 0) {
      terms.push(coef < 0 ? `-${core}` : core);
    } else {
      terms.push(coef < 0 ? `- ${core}` : `+ ${core}`);
    }
  };

  pushTerm(a, "x^2");
  pushTerm(b, "x");

  if (c !== 0) {
    if (terms.length === 0) {
      terms.push(`${c}`);
    } else {
      terms.push(c < 0 ? `- ${Math.abs(c)}` : `+ ${c}`);
    }
  }

  if (terms.length === 0) terms.push("0");
  return `y = ${terms.join(" ")}`;
}

function makeTaskData() {
  // Proporcje: 2 rozw. = 1/2, 1 rozw. = 1/3, 0 rozw. = 1/6
  const r = Math.random();
  const mode = r < 0.5 ? 2 : r < 5 / 6 ? 1 : 0;

  for (let tries = 0; tries < 250; tries += 1) {
    const qa = pickOne([-3, -2, -1, 1, 2, 3]);
    const ly = pickOne([-3, -2, -1, 1, 2, 3]); // wsp. przy y (nie zawsze 1)
    const qbBase = randInt(-6, 6);
    const qcBase = randInt(-8, 8);

    let lx;
    let lc;
    let qb;
    let qc;
    let points = [];
    let TA;
    let TB;
    let TC;

    TA = ly * qa;
    if (TA === 0) continue;

    if (mode === 2) {
      const x1 = randInt(-6, 6);
      let x2 = randInt(-6, 6);
      if (x2 === x1) continue;

      TB = -TA * (x1 + x2);
      TC = TA * x1 * x2;

      qb = qbBase;
      qc = qcBase;
      lx = TB - ly * qb;
      lc = ly * qc - TC;
      if (lx === 0) continue; // brak 0x

      const y1 = qa * x1 * x1 + qb * x1 + qc;
      const y2 = qa * x2 * x2 + qb * x2 + qc;
      points = [
        { x: x1, y: y1 },
        { x: x2, y: y2 },
      ].sort((p1, p2) => p1.x - p2.x);
    } else if (mode === 1) {
      const x0 = randInt(-6, 6);
      // (po podstawieniu) TA*(x-x0)^2=0
      TB = -2 * TA * x0;
      TC = TA * x0 * x0;

      qb = qbBase;
      qc = qcBase;
      lx = TB - ly * qb;
      lc = ly * qc - TC;
      if (lx === 0) continue;

      const y0 = qa * x0 * x0 + qb * x0 + qc;
      points = [{ x: x0, y: y0 }];
    } else {
      // delta < 0: TA*x^2 + TC = 0, gdzie TA i TC tego samego znaku
      const s = randInt(1, 6);
      TB = 0;
      TC = TA * s;

      qb = qbBase;
      qc = qcBase;
      lx = TB - ly * qb;
      lc = ly * qc - TC;
      if (lx === 0) continue;

      points = [];
    }

    if (Math.abs(qb) > 35 || Math.abs(qc) > 80) continue;
    if (Math.abs(lx) > 40 || Math.abs(lc) > 100) continue;
    if (points.some((p) => Math.abs(p.y) > 80)) continue;

    const quadLatex = polyYToLatex(qa, qb, qc);
    const lineLatex = linearExprLatex(lx, ly, lc);

    return {
      lx,
      ly,
      lc,
      qa,
      qb,
      qc,
      points,
      solutionCount: mode,
      quadLatex,
      lineLatex,
    };
  }

  return {
    lx: -1,
    ly: 1,
    lc: 0,
    qa: 1,
    qb: -1,
    qc: -2,
    points: [
      { x: -1, y: -1 },
      { x: 2, y: 2 },
    ],
    solutionCount: 2,
    quadLatex: "y = x^2 - x - 2",
    lineLatex: "-x + y = 0",
  };
}

export function generateTask() {
  return {
    type: "LinearQuadraticSubstitutionTask",
    data: makeTaskData(),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>Rozwiąż układ równań (liniowe i kwadratowe):</p>
      <BlockMath
        math={String.raw`\begin{cases}${data.quadLatex}\\${data.lineLatex}\end{cases}`}
      />
      <p>Najpierw wybierz liczbę rozwiązań, potem podaj odpowiednie pary.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const count = userAnswer?.count ?? "";
  return (
    <div className="flex flex-col gap-3">
      <SelectInput
        options={[
          { value: "0", label: "brak rozwiązań" },
          { value: "1", label: "jedno rozwiązanie" },
          { value: "2", label: "dwa rozwiązania" },
        ]}
        value={count}
        onChange={(e) =>
          onChange({ ...userAnswer, count: e?.target?.value ?? e })
        }
        placeholder="Wybierz liczbę rozwiązań"
        disabled={disabled}
      />

      {(count === "1" || count === "2") && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math={"x_1 ="} />
            <FractionInput
              value={userAnswer?.x1 || ""}
              onChange={(v) => onChange({ ...userAnswer, x1: v })}
              disabled={disabled}
              label="x_1"
              placeholder="np. -1"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"y_1 ="} />
            <FractionInput
              value={userAnswer?.y1 || ""}
              onChange={(v) => onChange({ ...userAnswer, y1: v })}
              disabled={disabled}
              label="y_1"
              placeholder="np. 2"
            />
          </div>
        </>
      )}

      {count === "2" && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math={"x_2 ="} />
            <FractionInput
              value={userAnswer?.x2 || ""}
              onChange={(v) => onChange({ ...userAnswer, x2: v })}
              disabled={disabled}
              label="x_2"
              placeholder="np. 3"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"y_2 ="} />
            <FractionInput
              value={userAnswer?.y2 || ""}
              onChange={(v) => onChange({ ...userAnswer, y2: v })}
              disabled={disabled}
              label="y_2"
              placeholder="np. -4"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const eps = 0.01;
  const count = data.solutionCount;
  const picked = userAnswer?.count;

  if (picked !== String(count)) return { correct: false };

  const x1Blank = isBlank(userAnswer?.x1);
  const y1Blank = isBlank(userAnswer?.y1);
  const x2Blank = isBlank(userAnswer?.x2);
  const y2Blank = isBlank(userAnswer?.y2);

  const ux1 = parseVal(userAnswer?.x1);
  const uy1 = parseVal(userAnswer?.y1);
  const ux2 = parseVal(userAnswer?.x2);
  const uy2 = parseVal(userAnswer?.y2);

  if (count === 0) {
    return { correct: x1Blank && y1Blank && x2Blank && y2Blank };
  }

  if (count === 1) {
    const p = data.points[0];
    const pair1Valid =
      Number.isFinite(ux1) &&
      Number.isFinite(uy1) &&
      Math.abs(ux1 - p.x) < eps &&
      Math.abs(uy1 - p.y) < eps;
    const pair2Valid =
      Number.isFinite(ux2) &&
      Number.isFinite(uy2) &&
      Math.abs(ux2 - p.x) < eps &&
      Math.abs(uy2 - p.y) < eps;

    if (pair1Valid && x2Blank && y2Blank) return { correct: true };
    if (pair2Valid && x1Blank && y1Blank) return { correct: true };
    if (pair1Valid && pair2Valid) return { correct: true };
    return { correct: false };
  }

  if (
    !Number.isFinite(ux1) ||
    !Number.isFinite(uy1) ||
    !Number.isFinite(ux2) ||
    !Number.isFinite(uy2)
  ) {
    return { correct: false };
  }

  const pA = data.points[0];
  const pB = data.points[1];

  const direct =
    Math.abs(ux1 - pA.x) < eps &&
    Math.abs(uy1 - pA.y) < eps &&
    Math.abs(ux2 - pB.x) < eps &&
    Math.abs(uy2 - pB.y) < eps;

  const swapped =
    Math.abs(ux1 - pB.x) < eps &&
    Math.abs(uy1 - pB.y) < eps &&
    Math.abs(ux2 - pA.x) < eps &&
    Math.abs(uy2 - pA.y) < eps;

  return { correct: direct || swapped };
}

export function renderAnswer({ data }) {
  const countLabel =
    data.solutionCount === 0
      ? "brak rozwiązań"
      : data.solutionCount === 1
      ? "jedno rozwiązanie"
      : "dwa rozwiązania";

  if (data.solutionCount === 0) {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.35 }}>
        <div>Liczba rozwiązań: {countLabel}</div>
        <div>
          <InlineMath math={"\\varnothing"} />
        </div>
      </div>
    );
  }

  if (data.solutionCount === 1) {
    const p = data.points[0];
    return (
      <div style={{ textAlign: "left", lineHeight: 1.35 }}>
        <div style={{ marginBottom: "0.2rem" }}>Liczba rozwiązań: {countLabel}</div>
        <InlineMath math={`(x,y)=(${p.x},${p.y})`} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.2rem" }}>Liczba rozwiązań: {countLabel}</div>
      <div>
        <InlineMath math={`(x_1,y_1)=(${data.points[0].x},${data.points[0].y})`} />
      </div>
      <div>
        <InlineMath math={`(x_2,y_2)=(${data.points[1].x},${data.points[1].y})`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const userAnswer =
    maybeUserAnswer && typeof maybeUserAnswer === "object"
      ? maybeUserAnswer
      : taskOrUserAnswer && typeof taskOrUserAnswer === "object"
      ? taskOrUserAnswer
      : {};

  const picked = userAnswer?.count;
  const rawOf = (v) => (v && typeof v === "object" && "raw" in v ? v.raw : v) || "";
  const hasAny =
    picked ||
    rawOf(userAnswer?.x1) ||
    rawOf(userAnswer?.y1) ||
    rawOf(userAnswer?.x2) ||
    rawOf(userAnswer?.y2);

  if (!hasAny) return <i>(brak Odpowiedźi)</i>;

  const x1 = rawOf(userAnswer?.x1) || "?";
  const y1 = rawOf(userAnswer?.y1) || "?";
  const x2 = rawOf(userAnswer?.x2) || "?";
  const y2 = rawOf(userAnswer?.y2) || "?";

  const showOne = picked === "1";
  const showTwo = picked === "2";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.2rem" }}>
        {picked === "0"
          ? "Liczba rozwiązań: brak rozwiązań"
          : picked === "1"
          ? "Liczba rozwiązań: jedno rozwiązanie"
          : picked === "2"
          ? "Liczba rozwiązań: dwa rozwiązania"
          : "Liczba rozwiązań: (brak wyboru)"}
      </div>
      {showOne && (
        <>
          <div>
            <InlineMath math={formatLatexPreview(x1, "x_1")} />
          </div>
          <div>
            <InlineMath math={formatLatexPreview(y1, "y_1")} />
          </div>
        </>
      )}
      {showTwo && (
        <>
          <div>
            <InlineMath math={formatLatexPreview(x1, "x_1")} />
          </div>
          <div>
            <InlineMath math={formatLatexPreview(y1, "y_1")} />
          </div>
          <div>
            <InlineMath math={formatLatexPreview(x2, "x_2")} />
          </div>
          <div>
            <InlineMath math={formatLatexPreview(y2, "y_2")} />
          </div>
        </>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { lx, ly, lc, qa, qb, qc, points } = data;
  const yExpr = polyYToLatex(qa, qb, qc).replace("y = ", "");

  const A = ly * qa;
  const B = lx + ly * qb;
  const C = ly * qc - lc;
  const delta = B * B - 4 * A * C;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Podstawiamy{" "}
        <InlineMath math={`y=${yExpr}`} />{" "}
        do równania liniowego:
      </p>
      <BlockMath math={linearExprLatex(lx, ly, lc)} />
      <BlockMath math={substitutionEqLatex(lx, ly, yExpr, lc)} />
      <BlockMath math={`${A}x^2 ${fmtSigned(B)}x ${fmtSigned(C)} = 0`} />

      <p>
        <strong>Krok 2:</strong> Rozwiązujemy równanie kwadratowe (delta):
      </p>
      <BlockMath math={`\\Delta = (${B})^2 - 4\\cdot(${A})\\cdot(${C}) = ${delta}`} />

      {delta < 0 && (
        <>
          <p>
            Ponieważ <InlineMath math={"\\Delta < 0"} />, równanie kwadratowe nie
            ma rozwiązań rzeczywistych.
          </p>
          <p>
            <strong>Odpowiedź:</strong> <InlineMath math={"\\varnothing"} />.
          </p>
        </>
      )}

      {delta === 0 && (
        <>
          <BlockMath math={`x_0 = \\dfrac{-(${B})}{2\\cdot(${A})} = ${points[0].x}`} />
          <BlockMath
            math={`y_0 = ${qa}\\cdot(${points[0].x})^2 ${fmtSigned(qb * points[0].x)} ${fmtSigned(qc)} = ${points[0].y}`}
          />
          <p>
            <strong>Odpowiedź:</strong>{" "}
            <InlineMath math={`(x,y)=(${points[0].x},${points[0].y})`} />.
          </p>
        </>
      )}

      {delta > 0 && (
        <>
          <BlockMath math={`x_1 = ${points[0].x},\\ x_2 = ${points[1].x}`} />
          <BlockMath
            math={`y_1 = ${qa}\\cdot(${points[0].x})^2 ${fmtSigned(qb * points[0].x)} ${fmtSigned(qc)} = ${points[0].y}`}
          />
          <BlockMath
            math={`y_2 = ${qa}\\cdot(${points[1].x})^2 ${fmtSigned(qb * points[1].x)} ${fmtSigned(qc)} = ${points[1].y}`}
          />
          <p>
            <strong>Odpowiedź:</strong>{" "}
            <InlineMath
              math={`\\{(${points[0].x},${points[0].y}),\\ (${points[1].x},${points[1].y})\\}`}
            />
            .
          </p>
        </>
      )}
    </div>
  );
}


